/**
 * useShareAnalysis Hook
 *
 * Custom hook for sharing analysis results.
 * Features:
 * - React Native Share API integration
 * - Clipboard fallback for iOS quirks
 * - Haptic feedback on share
 * - Error handling
 * - Loading states
 * - Platform-specific behavior
 */
import { useState, useCallback } from 'react';
import { Share, Platform, Alert } from 'react-native';
import * as Clipboard from 'expo-clipboard';
import { triggerImpact } from '../../lib/haptics';
import * as Haptics from 'expo-haptics';
import type { DramaAnalysisResponse } from '@dramadetector/shared';
import { useSDUIConfig, toUISpecTier } from '@dramadetector/ui/json-render';
import { useEntitlements } from '../../hooks/useEntitlements';
import { useSharingEnabledFlag } from '../../hooks/useFeatureFlags';
import { formatShareText } from './formatters';

/**
 * Share result types
 */
type ShareResult = 'shared' | 'dismissed' | 'copied' | 'error';

/**
 * Share options for customizing share behavior
 */
interface ShareOptions {
  /**
   * Override the default formatted share text with custom viral template
   */
  overrideText?: string;
}

/**
 * Clipboard interface for dependency injection (enables testing)
 */
interface ClipboardInterface {
  setStringAsync: (text: string) => Promise<boolean | void>;
}

/**
 * Hook configuration options
 */
interface UseShareAnalysisConfig {
  /**
   * Clipboard implementation (default: expo-clipboard)
   * Allows tests to inject mock clipboard for edge case testing
   */
  clipboard?: ClipboardInterface;
}

/**
 * Hook return type
 */
interface UseShareAnalysisReturn {
  /**
   * Share the analysis
   * @param analysis - Analysis result to share
   * @param options - Optional share customization options
   */
  shareAnalysis: (
    analysis: DramaAnalysisResponse,
    options?: ShareOptions
  ) => Promise<ShareResult>;

  /**
   * Whether a share operation is in progress
   */
  isSharing: boolean;

  /**
   * Error message if share failed
   */
  error: string | null;
}

/**
 * Check if Share API is available
 */
function isShareAvailable(): boolean {
  try {
    return typeof Share !== 'undefined' && typeof Share.share === 'function';
  } catch {
    return false;
  }
}

/**
 * Copy text to clipboard as fallback
 * @param text - Text to copy
 * @param clipboard - Clipboard implementation (for testing)
 */
async function copyToClipboard(
  text: string,
  clipboard: ClipboardInterface = Clipboard
): Promise<void> {
  try {
    await clipboard.setStringAsync(text);
    Alert.alert(
      'Copied!',
      'Analysis copied to clipboard. You can now paste it anywhere.',
      [{ text: 'OK' }]
    );
  } catch (error) {
    throw new Error('Failed to copy to clipboard');
  }
}


/**
 * Custom hook for sharing analysis results
 *
 * @param config - Optional configuration (for testing)
 * @returns Share analysis hook with shareAnalysis function, loading state, and error
 *
 * @example
 * ```tsx
 * function MyComponent() {
 *   const { shareAnalysis, isSharing, error } = useShareAnalysis();
 *   const { currentAnalysis } = useAnalysisStore();
 *
 *   const handleShare = async () => {
 *     if (!currentAnalysis?.result) return;
 *     const result = await shareAnalysis(currentAnalysis.result);
 *     console.log('Share result:', result);
 *   };
 *
 *   return (
 *     <button onClick={handleShare} disabled={isSharing}>
 *       {isSharing ? 'Sharing...' : 'Share'}
 *     </button>
 *   );
 * }
 * ```
 *
 * @example Testing with mock clipboard
 * ```tsx
 * const mockClipboard = { setStringAsync: jest.fn() };
 * const { result } = renderHook(() => useShareAnalysis({ clipboard: mockClipboard }));
 * mockClipboard.setStringAsync.mockRejectedValueOnce(new Error('Clipboard failed'));
 * ```
 */
export function useShareAnalysis(config?: UseShareAnalysisConfig): UseShareAnalysisReturn {
  const clipboard = config?.clipboard || Clipboard;
  const [isSharing, setIsSharing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Kill switch: defensive second layer on top of the ViralShareButton gating
  const sharingEnabled = useSharingEnabledFlag();

  // i18n exception: SDUI copy is pre-localized, falls back to default when null
  const { tier } = useEntitlements();
  const { config: sduiConfig } = useSDUIConfig('referral-share', toUISpecTier(tier));

  const shareAnalysis = useCallback(async (
    analysis: DramaAnalysisResponse,
    options?: ShareOptions
  ): Promise<ShareResult> => {
    // Reset error state
    setError(null);

    if (!sharingEnabled) {
      // Defensive layer — the ViralShareButton already hides when this flag is off,
      // so no user ever sees this path. Log a breadcrumb and bail without a
      // user-facing message (hence no i18n string needed here).
      console.warn('[useShareAnalysis] ff_sharing_enabled is off — aborting share');
      return 'error';
    }

    // Validate input
    if (!analysis) {
      const errorMsg = 'No analysis to share';
      setError(errorMsg);
      return 'error';
    }

    setIsSharing(true);

    // Format analysis as shareable text once (used in both try and catch)
    // Use override text if provided (e.g., from ViralShareButton templates)
    // Otherwise, use default formatter
    const shareText = options?.overrideText || formatShareText(analysis, sduiConfig.shareFooter ?? undefined);

    if (!shareText) {
      setError('Failed to format analysis for sharing');
      setIsSharing(false);
      return 'error';
    }

    try {
      // Trigger haptic feedback
      triggerImpact(Haptics.ImpactFeedbackStyle.Light);

      // Check if Share API is available
      if (!isShareAvailable()) {
        // Fallback to clipboard
        await copyToClipboard(shareText, clipboard);
        return 'copied';
      }

      // Platform-specific share options
      const shareOptions = {
        message: shareText,
        ...(Platform.OS === 'ios' ? { subject: 'Drama Analysis' } : {}),
      };

      // Share using native Share API
      const result = await Share.share(shareOptions);

      if (result.action === Share.sharedAction) {
        // Content was shared
        return 'shared';
      } else if (result.action === Share.dismissedAction) {
        // Share dialog was dismissed
        return 'dismissed';
      }

      return 'shared';
    } catch (err) {
      // Handle specific error types
      const errorMessage = err instanceof Error ? err.message : 'Failed to share analysis';

      // Some share cancellations throw errors on Android
      // Don't treat cancellation as error
      if (errorMessage.includes('User did not share') ||
          errorMessage.includes('cancelled')) {
        return 'dismissed';
      }

      // Try clipboard fallback on error (using the same shareText from above)
      try {
        await copyToClipboard(shareText, clipboard);
        return 'copied';
      } catch (clipboardError) {
        // Both share and clipboard failed
        setError(errorMessage);
        Alert.alert(
          'Share Failed',
          'Unable to share analysis. Please try again.',
          [{ text: 'OK' }]
        );
        return 'error';
      }
    } finally {
      setIsSharing(false);
    }
  }, [clipboard, sduiConfig, sharingEnabled]);

  return {
    shareAnalysis,
    isSharing,
    error,
  };
}
